import React, { lazy, Suspense, useEffect, useState } from 'react'
import { notifyError } from '../../utils/notifyError'
import LoadingPageV2 from '../LoadingPageV2'
import PageHeader from '../../components/PageHeader'
import { CreateAndEditTerms } from '../../features/Terms/CreateAndEditTerms'
import { Box, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FlaggingAndSummarizationManager } from '../../APIManager'
import { type Row, type ApplyFiltersAndDispatchParams } from '../../components/CustomTable/types'
import { useSearchParams } from 'react-router-dom'
import CustomTooltip from '../../components/CustomTooltip'
import { grey } from '../../colors'
import { exportCsv } from '../../utils/exportCsv'
import { notification, closeNotification } from '../../components/Notifications'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

export const SavedComments: React.FC = () => {
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ pageSize: 5 })

  const { data: SavedCommentsData, isFetching: isSavingCommentsLoading, isError: isSavedCommentsError, error: savedCommentsError } = useQuery({
    queryKey: ['savedCommentsCDG', params],
    queryFn: async () => {
      const { data } = await FlaggingAndSummarizationManager.getFlaggedCommentsCDG(params)
      return data
    },
    retry: false
  })

  useEffect(() => {
    if (isSavedCommentsError) notifyError(savedCommentsError, { fallbackMessage: 'Failed to load saved comments. Please try again.' })
  }, [isSavedCommentsError])

  const overrides = {
    commentText: (row: Row) => {
      const { commentText } = row
      return <CustomTooltip title={commentText}>
        <Typography
          component={'span'}
          fontWeight='500'
          fontSize='14px'
          color={grey[250]}
        >{commentText}</Typography>
      </CustomTooltip>
    }
  }

  const { mutate: exportSectionCSVMutate, isPending: exportLoading } = useMutation({
    mutationFn: FlaggingAndSummarizationManager.exportSavedCommentsCSV,
    onMutate: () => notification('Preparing download...', { variant: 'loading', persist: true }),
    onSuccess: (response, _variables, context) => {
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: context })
      } else {
        closeNotification(context)
      }
    },
    onError: (error: any, _variables, context) => {
      closeNotification(context)
      notification(error?.message ?? 'Export csv failed', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const handleExport = (): void => {
    exportSectionCSVMutate()
  }

  return (
    <>
      <Suspense fallback={<LoadingPageV2 />}>
        <Box mt={3}>
          <PageHeader
            title={'Saved Comments'}
            showBackButton
          />
          <Box sx={{ mt: 12 }}>
            <AppTable
              createAction={<CreateAndEditTerms />}
              exportCsv={handleExport}
              list={SavedCommentsData}
              loading={isSavingCommentsLoading}
              onSortModelChange={(sortModel) => { }}
              params={params}
              setParams={setParams}
              overrides={overrides}
               exportButtonProps={{ loading: exportLoading, disabled: exportLoading }}
            />
          </Box>
        </Box>
      </Suspense>
    </>
  )
}
